(function () {
  'use strict';

  const HIDDEN_CLASS = 'variant-media-group--hidden';
  const STASH_SELECTOR = '[data-variant-media-stash]';
  const THUMB_STASH_SELECTOR = '[data-variant-media-thumb-stash]';
  const SLIDER_SELECTOR = '[data-product-single-media-slider]';
  const THUMBS_SELECTOR = '[data-product-single-media-thumbs]';
  const WRAPPER_SELECTOR = '[data-product-single-media-wrapper]';
  const THUMB_SELECTOR = '.product-single__thumbnail';
  const CONFIG_SELECTOR = '[data-variant-media-config]';

  function parseConfig(gallery) {
    const configNode = gallery?.querySelector(CONFIG_SELECTOR);
    if (!configNode?.textContent) return null;

    try {
      return JSON.parse(configNode.textContent);
    } catch (error) {
      console.warn('Variant media config parse error:', error);
      return null;
    }
  }

  function isGroupingEnabled(gallery) {
    return gallery?.dataset?.variantMediaGroupsEnabled === 'true';
  }

  function getVisibleMediaIds(config, variant) {
    if (!config || !variant) return null;

    if (!variant.featured_media?.id) {
      return null;
    }

    const anchorId = String(
      config.variants?.[String(variant.id)] || variant.featured_media.id
    );

    const groupIds = config.groups?.[anchorId];
    if (!groupIds || !groupIds.length) {
      return null;
    }

    return groupIds.map(String);
  }

  function usesCarousel(productInfo) {
    return !productInfo.tallLayout || window.innerWidth < window.theme.sizes.large;
  }

  function sortByMediaIndex(elements) {
    return [...elements].sort((left, right) => {
      const leftIndex = parseInt(left.dataset.mediaSortIndex || '0', 10);
      const rightIndex = parseInt(right.dataset.mediaSortIndex || '0', 10);
      return leftIndex - rightIndex;
    });
  }

  function getAllElements(container, stash, selector) {
    if (!container || !stash) return [];

    return [
      ...container.querySelectorAll(selector),
      ...stash.querySelectorAll(selector),
    ];
  }

  function moveElements(visibleIds, container, stash, selector) {
    const elements = getAllElements(container, stash, selector);
    const visible = [];
    const hidden = [];

    elements.forEach((element) => {
      const mediaId = String(element.getAttribute('data-id') || '');
      const show = visibleIds === null || visibleIds.includes(mediaId);

      if (show) {
        visible.push(element);
      } else {
        hidden.push(element);
      }
    });

    sortByMediaIndex(hidden).forEach((element) => stash.appendChild(element));
    sortByMediaIndex(visible).forEach((element) => container.appendChild(element));

    return visible.length;
  }

  function toggleElements(visibleIds, container, selector) {
    const elements = container.querySelectorAll(selector);

    elements.forEach((element) => {
      const mediaId = String(element.getAttribute('data-id') || '');
      const show = visibleIds === null || visibleIds.includes(mediaId);
      element.classList.toggle(HIDDEN_CLASS, !show);
    });
  }

  function reorderFeaturedFirst(container, featuredMediaId, selector) {
    if (!featuredMediaId || !container) return;

    const featured = [...container.querySelectorAll(selector)].find((element) => {
      return String(element.getAttribute('data-id')) === String(featuredMediaId);
    });

    if (featured && featured.parentElement === container) {
      container.insertBefore(featured, container.firstChild);
    }
  }

  function resetSlideVisibility(slider, featuredMediaId) {
    if (!slider) return;

    slider.querySelectorAll(WRAPPER_SELECTOR).forEach((slide) => {
      slide.classList.add('media--hidden');
    });

    const featuredSlide = slider.querySelector(`${WRAPPER_SELECTOR}[data-id="${featuredMediaId}"]`);
    if (featuredSlide) {
      featuredSlide.classList.remove('media--hidden');
    }
  }

  function safeDestroySlider(productInfo) {
    if (productInfo.flkty) {
      productInfo.flkty.destroy();
      productInfo.flkty = null;
    }

    if (productInfo.flktyNav) {
      productInfo.flktyNav.destroy();
      productInfo.flktyNav = null;
    }
  }

  function filterVariantMedia(productInfo, variant) {
    const gallery = productInfo.querySelector('[data-product-single-media-group]');
    if (!gallery || !isGroupingEnabled(gallery)) return;

    const config = parseConfig(gallery);
    const visibleIds = getVisibleMediaIds(config, variant);
    const slider = gallery.querySelector(SLIDER_SELECTOR);
    const thumbs = gallery.querySelector(THUMBS_SELECTOR);
    const mediaStash = gallery.querySelector(STASH_SELECTOR);
    const thumbStash = gallery.querySelector(THUMB_STASH_SELECTOR);

    if (!slider || !mediaStash) return;

    const carouselMode = usesCarousel(productInfo);
    let visibleMediaCount = 0;

    if (productInfo.flkty) {
      safeDestroySlider(productInfo);
    }

    if (carouselMode) {
      visibleMediaCount = moveElements(visibleIds, slider, mediaStash, WRAPPER_SELECTOR);

      if (thumbs && thumbStash) {
        moveElements(visibleIds, thumbs, thumbStash, THUMB_SELECTOR);
      }
    } else {
      gallery.querySelectorAll(`${WRAPPER_SELECTOR}, ${THUMB_SELECTOR}`).forEach((element) => {
        element.classList.remove(HIDDEN_CLASS);
      });

      toggleElements(visibleIds, slider, WRAPPER_SELECTOR);

      if (thumbs) {
        toggleElements(visibleIds, thumbs, THUMB_SELECTOR);
      }

      visibleMediaCount = slider.querySelectorAll(`${WRAPPER_SELECTOR}:not(.${HIDDEN_CLASS})`).length;
    }

    if (variant?.featured_media?.id) {
      reorderFeaturedFirst(slider, variant.featured_media.id, WRAPPER_SELECTOR);

      if (thumbs) {
        reorderFeaturedFirst(thumbs, variant.featured_media.id, THUMB_SELECTOR);
      }

      if (!carouselMode) {
        resetSlideVisibility(slider, variant.featured_media.id);
      }
    } else if (visibleIds === null) {
      const firstSlide = slider.querySelector(WRAPPER_SELECTOR);

      if (!carouselMode) {
        slider.querySelectorAll(WRAPPER_SELECTOR).forEach((slide, index) => {
          slide.classList.toggle('media--hidden', index !== 0);
        });
      }

      if (firstSlide) {
        reorderFeaturedFirst(slider, firstSlide.getAttribute('data-id'), WRAPPER_SELECTOR);
      }
    }

    if (carouselMode && visibleMediaCount > 1 && typeof productInfo.initProductSlider === 'function') {
      productInfo.initProductSlider();

      if (variant?.featured_media && typeof productInfo.updateMedia === 'function') {
        productInfo.updateMedia(variant.featured_media);
      }
    } else if (variant?.featured_media && typeof productInfo.updateMedia === 'function') {
      requestAnimationFrame(() => {
        productInfo.updateMedia(variant.featured_media);
      });
    }
  }

  function getVariantFromProductInfo(productInfo) {
    const variantScript = productInfo.querySelector('variant-selects [data-selected-variant]');
    if (!variantScript?.textContent) return null;

    try {
      return JSON.parse(variantScript.textContent);
    } catch (error) {
      return null;
    }
  }

  function belongsToProductInfo(productInfo, data) {
    if (!data?.variant?.id) return false;

    const variantInput = productInfo.querySelector('input[name="id"]');
    if (variantInput && String(variantInput.value) === String(data.variant.id)) {
      return true;
    }

    const sectionId = String(productInfo.dataset.originalSection || productInfo.dataset.section || '');
    return sectionId !== '' && String(data.sectionId) === sectionId;
  }

  function initProductInfo(productInfo) {
    const gallery = productInfo.querySelector('[data-product-single-media-group]');
    if (!gallery || !isGroupingEnabled(gallery)) return;

    const applyFilter = () => {
      const variant = getVariantFromProductInfo(productInfo);
      if (variant) {
        filterVariantMedia(productInfo, variant);
      }
    };

    if (productInfo._variantMediaGroupsUnsub) {
      productInfo._variantMediaGroupsUnsub();
    }

    if (productInfo._variantMediaGroupsResizeHandler) {
      window.removeEventListener('theme:resize:width', productInfo._variantMediaGroupsResizeHandler);
    }

    productInfo._variantMediaGroupsUnsub = window.subscribe(
      window.theme.PUB_SUB_EVENTS.variantChange,
      (event) => {
        if (!belongsToProductInfo(productInfo, event?.data)) return;
        filterVariantMedia(productInfo, event.data.variant);
      }
    );

    productInfo._variantMediaGroupsResizeHandler = () => {
      const variant = getVariantFromProductInfo(productInfo);
      if (variant) {
        filterVariantMedia(productInfo, variant);
      }
    };

    window.addEventListener('theme:resize:width', productInfo._variantMediaGroupsResizeHandler);

    requestAnimationFrame(applyFilter);
  }

  function onProductInfoLoaded(event) {
    if (event.target?.matches?.('product-info')) {
      initProductInfo(event.target);
    }
  }

  document.addEventListener('theme:product-info:loaded', onProductInfoLoaded);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      document.querySelectorAll('product-info').forEach(initProductInfo);
    });
  } else {
    document.querySelectorAll('product-info').forEach(initProductInfo);
  }
})();
